import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { track } from '@/lib/analytics';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import type { Database } from '@/integrations/supabase/types';

type ArchiveReason = Database['public']['Enums']['patient_archive_reason'];

interface ArchivePatientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  patientName: string;
  onArchived?: () => void;
}

export function ArchivePatientDialog({
  open,
  onOpenChange,
  patientId,
  patientName,
  onArchived,
}: ArchivePatientDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState<ArchiveReason>('discharged');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleArchive = async () => {
    setIsSubmitting(true);
    try {
      // Compute days since last shift for analytics
      const { data: lastShift } = await supabase
        .from('shifts')
        .select('check_in_at')
        .eq('patient_id', patientId)
        .order('check_in_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      const daysSinceLastShift = lastShift?.check_in_at
        ? Math.floor((Date.now() - new Date(lastShift.check_in_at).getTime()) / 86400000)
        : null;

      const { error } = await supabase
        .from('patients')
        .update({
          status: 'archived',
          archived_reason: reason,
          archive_notes: notes || null,
        } as any)
        .eq('id', patientId);

      if (error) throw error;

      track('patient_archived', {
        patient_id: patientId,
        reason,
        days_since_last_shift: daysSinceLastShift,
      });

      toast.success(t('patient.archive.success', { defaultValue: 'Paciente arquivado' }));
      await queryClient.invalidateQueries();
      onOpenChange(false);
      setNotes('');
      setReason('discharged');
      onArchived?.();
    } catch (e: any) {
      console.error('Error archiving patient:', e);
      toast.error(t('common.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('patient.archive.title', { defaultValue: 'Arquivar paciente' })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('patient.archive.description', {
              name: patientName,
              defaultValue: `Você está prestes a arquivar ${patientName}.`,
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="archive-reason">
              {t('patient.archive.reasonLabel', { defaultValue: 'Motivo' })}
            </Label>
            <Select value={reason} onValueChange={(v) => setReason(v as ArchiveReason)}>
              <SelectTrigger id="archive-reason">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="discharged">
                  {t('patient.archive.reason.discharged', { defaultValue: 'Alta' })}
                </SelectItem>
                <SelectItem value="deceased">
                  {t('patient.archive.reason.deceased', { defaultValue: 'Óbito' })}
                </SelectItem>
                <SelectItem value="transferred">
                  {t('patient.archive.reason.transferred', { defaultValue: 'Transferência' })}
                </SelectItem>
                <SelectItem value="other">
                  {t('patient.archive.reason.other', { defaultValue: 'Outro' })}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="archive-notes">
              {t('patient.archive.notesLabel', { defaultValue: 'Observações (opcional)' })}
            </Label>
            <Textarea
              id="archive-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('patient.archive.notesPlaceholder', {
                defaultValue: 'Informações adicionais sobre o arquivamento',
              })}
              rows={3}
            />
          </div>

          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="text-xs">
              {t('patient.archive.warning', {
                defaultValue:
                  'Cuidadores e observadores serão desvinculados. Plantões agendados serão cancelados. O histórico permanece acessível somente leitura. O slot de assinatura é liberado imediatamente. Você pode desarquivar a qualquer momento.',
              })}
            </AlertDescription>
          </Alert>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isSubmitting}>
            {t('common.cancel')}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              handleArchive();
            }}
            disabled={isSubmitting}
            className="bg-destructive hover:bg-destructive/90"
          >
            {isSubmitting
              ? t('common.loading')
              : t('patient.archive.confirm', { defaultValue: 'Arquivar paciente' })}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
